import React, { useCallback, useMemo, useState } from 'react';
import { MsalProvider } from '@azure/msal-react';
import {
  GlobalErrorContext,
  IGlobalErrorModalContent,
} from './globalErrorContext';
import { msalInstance } from 'msalConfig/msalConfig';
import { HelmetProvider } from 'react-helmet-async';
import { Provider } from 'react-redux';
import { HashRouter } from 'react-router-dom';
import store from 'services/store';
import { GlobalErrorModal } from 'app/components/GlobalErrorModal';
import { I18nextProvider, useTranslation } from 'react-i18next';
import { LanguageProvider } from 'context';

interface Props {
  children: React.ReactNode;
}

// TODO: Perform a proper refactor of the context provider architecture.
// The `ServiceProviders` and `BusinessLogicProviders` components below are a temporary
// first-pass refactor. They were created by simply splitting the monolithic `ReactContextContainer`
// to improve the separation of concerns. Further work is needed to clean up dependencies
// and streamline the initialization flow.
export default function ServiceProviders({ children }: Props) {
  const { i18n } = useTranslation();

  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalContent, setErrorModalContent] =
    useState<IGlobalErrorModalContent>();

  const addErrorModal = useCallback((content?: IGlobalErrorModalContent) => {
    setErrorModalContent(content);
    setShowErrorModal(true);
  }, []);

  const closeErrorModal = useCallback(() => {
    setShowErrorModal(false);
    setErrorModalContent(undefined);
  }, []);

  const globalErrorContextValue = useMemo(
    () => ({
      addErrorModal,
      closeErrorModal,
      showErrorModal,
      errorModalContent,
    }),
    [addErrorModal, closeErrorModal, showErrorModal, errorModalContent],
  );

  return (
    <Provider store={store}>
      <HelmetProvider>
        <MsalProvider instance={msalInstance}>
          <I18nextProvider i18n={i18n}>
            <LanguageProvider>
              <GlobalErrorContext.Provider value={globalErrorContextValue}>
                {showErrorModal && (
                  <GlobalErrorModal
                    onClose={closeErrorModal}
                    title={errorModalContent?.title}
                    description={errorModalContent?.description}
                  />
                )}

                <HashRouter>{children}</HashRouter>
              </GlobalErrorContext.Provider>
            </LanguageProvider>
          </I18nextProvider>
        </MsalProvider>
      </HelmetProvider>
    </Provider>
  );
}
